"use client";

import NumberFlow from "@number-flow/react";
import { Check, Link as LinkIcon } from "iconoir-react";
import { useTranslations } from "next-intl";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { CtaButton } from "@/components/ui/cta-button";
import { Link } from "@/components/ui/link";
import { FINANCING_SIGNUP_URL } from "@/lib/financing/constants";
import { todayIso } from "@/lib/pricing-calculator/defaults";
import { FORECAST_HORIZON_MONTHS } from "@/lib/pricing-calculator/model";
import type { SimulationResult } from "@/lib/pricing-calculator/model";
import type { CalculatorState } from "@/lib/pricing-calculator/url-state";
import { searchParamsFromState } from "@/lib/pricing-calculator/url-state";
import { cn } from "@/lib/utils";

import { useCalculatorEnvironment } from "./context";
import { Card } from "./controls";
import type { FrameConnectors } from "./controls";

interface ResultsSummaryProps {
  state: CalculatorState;
  result: SimulationResult;
  feePerMonth: number | null;
  months: number | null;
  className?: string;
  connectors?: FrameConnectors;
}

function Stat({
  label,
  value,
  note,
  emphasis = false,
  muted = false,
}: {
  label: string;
  value: React.ReactNode;
  note: React.ReactNode;
  emphasis?: boolean;
  /** Quieter value styling for figures that are secondary context. */
  muted?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <dt className="text-ds-label-lg text-grey-500">{label}</dt>
      <dd
        // Single line at a fixed size so a long date (23 Sept 2027) or a
        // wide figure never wraps and shifts the content below.
        className={cn(
          "text-ds-heading-h5 text-grey-950 whitespace-nowrap tabular-nums",
          emphasis && "text-pulse-700",
          muted && "text-grey-600"
        )}
      >
        {value}
      </dd>
      <dd className="text-ds-body-xs text-grey-500">{note}</dd>
    </div>
  );
}

export function ResultsSummary({
  state,
  result,
  feePerMonth,
  months,
  className,
  connectors = "none",
}: ResultsSummaryProps) {
  const t = useTranslations("pages.pricing.calculator.results");
  const { format, locale, currency } = useCalculatorEnvironment();
  const [copied, setCopied] = React.useState(false);
  const [animate, setAnimate] = React.useState(false);

  // Let the first paint settle on the real figure before animating changes.
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const copyLink = async () => {
    try {
      // Built from the live state rather than the address bar, which syncs on
      // a debounce, and without the sender's own UTMs.
      const query = searchParamsFromState(state, todayIso()).toString();
      const url = `${window.location.origin}${window.location.pathname}${query ? `?${query}` : ""}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context or permissions): keep quiet.
    }
  };

  const isTermLoan = state.structure === "term-loan";
  const firstPayment = result.rows[0]?.payment ?? 0;

  const monthsLabel = (() => {
    if (months === null) return null;
    if (isTermLoan) return t("monthsValue", { count: months });
    return t("estimatedMonths", { count: format.number(months, 1) });
  })();

  const paidOffValue = result.payoff
    ? format.date(result.payoff)
    : t("unavailable");
  const paidOffNote = (() => {
    if (!result.complete || result.days === null || monthsLabel === null) {
      return t("paidOffNote.incomplete", { count: FORECAST_HORIZON_MONTHS });
    }
    return t(isTermLoan ? "paidOffNote.termLoan" : "paidOffNote.cashAdvance", {
      months: monthsLabel,
      days: format.number(result.days),
    });
  })();

  const repaymentNote = isTermLoan
    ? t("repaymentNote.termLoan")
    : state.capEnabled
      ? t("repaymentNote.cashAdvanceCapped")
      : t("repaymentNote.cashAdvance");

  return (
    <Card
      // Raised above the stacked panel below so the CTA's soft glow is not
      // clipped by the next panel's white background.
      className={cn("z-[1] flex flex-col gap-8", className)}
      connectors={connectors}
      data-testid="pricing-results-summary"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-3">
            <p className="text-ds-body-md text-grey-500">{t("totalLabel")}</p>
            <p
              className="text-grey-950 text-[40px] leading-[1.05] tracking-[-0.04em] tabular-nums sm:text-[52px] lg:text-[64px]"
              aria-live="polite"
              aria-atomic
            >
              <NumberFlow
                // Remount on a currency switch so the new sign appears at
                // once; only changes to the figure itself animate.
                key={currency}
                value={result.total}
                locales={locale}
                format={{
                  style: "currency",
                  currency,
                  currencyDisplay: "narrowSymbol",
                  maximumFractionDigits: 0,
                }}
                animated={animate}
                willChange
              />
            </p>
            <p className="text-ds-body-md text-grey-600 tabular-nums">
              {t("totalBreakdown", {
                amount: format.money(result.principal),
                fee: format.money(result.fee),
              })}
            </p>
          </div>
          <span className="bg-pulse-50 text-pulse-700 text-ds-label-lg rounded-ds-full px-3 py-1.5 font-medium tabular-nums">
            {t("feeBadge", {
              fee: format.percent(
                state.feePercent,
                state.feePercent % 1 === 0 ? 0 : 1
              ),
            })}
          </span>
        </div>

        {/* Stacked, then three-up in one step at `md`: two-up would leave the
            odd cell alone on a second row, and three columns below `md` are
            too narrow for a date like "24 Jun 2027" to stay on one line. */}
        <dl className="border-grey-100 grid grid-cols-1 gap-6 border-t pt-6 md:grid-cols-3 md:gap-8">
          <Stat
            label={t(`repaymentLabel.${state.frequency}`)}
            value={format.money(firstPayment, 2)}
            note={repaymentNote}
          />
          <Stat
            label={t("paidOffLabel")}
            value={paidOffValue}
            note={paidOffNote}
          />
          <Stat
            label={t("feePerMonthLabel")}
            value={
              feePerMonth === null
                ? t("unavailable")
                : format.percent(feePerMonth, 2)
            }
            note={
              feePerMonth === null || monthsLabel === null
                ? t("feePerMonthUnavailable")
                : t("feePerMonthNote", {
                    fee: format.percent(
                      state.feePercent,
                      state.feePercent % 1 === 0 ? 0 : 1
                    ),
                    months: monthsLabel,
                  })
            }
            emphasis
          />
          {/* Estimated APR is withheld from v1 pending sign-off. The model
              still computes `result.apr` and the `aprLabel` / `aprNote` /
              `aprUnavailable` strings are kept, so restoring it is a matter
              of reinstating this tile and widening the grid back to four. */}
        </dl>
      </div>

      {!result.complete && (
        <p
          role="status"
          className="text-ds-body-sm rounded-ds-l bg-orange-50 px-4 py-3 text-orange-900"
        >
          {t("incompleteNotice", {
            balance: format.money(result.balance),
            count: FORECAST_HORIZON_MONTHS,
          })}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <CtaButton
          variant="pulse"
          size="lg"
          render={<Link href={FINANCING_SIGNUP_URL} external />}
        >
          {t("applyCta")}
        </CtaButton>
        <Button
          type="button"
          variant="secondary"
          size="lg"
          onClick={copyLink}
          aria-live="polite"
        >
          {copied ? (
            <Check aria-hidden className="text-green-700" />
          ) : (
            <LinkIcon aria-hidden />
          )}
          {copied ? t("copied") : t("copyLink")}
        </Button>
      </div>
    </Card>
  );
}
